import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import clsx from 'clsx';
import type { GalleryMedia } from '../../types';
import { ProgressiveSlideImage } from './ProgressiveSlideImage';
import { ActionPanel } from './ActionPanel';
import { OptimizedImage } from '../../../OptimizedImage';
import styles from './MobileCarousel.module.css';

interface MobileCarouselProps {
  image: GalleryMedia;
  images: GalleryMedia[];
  currentIndex: number;
  showActionPanel: boolean;
  isVideo: boolean;
  isSvg: boolean;
  canRegenerate: boolean;
  isDownloading: boolean;
  confirmingDelete: boolean;
  // Refs
  pullRef: React.RefObject<HTMLDivElement | null>;
  mobileCarouselRef: React.MutableRefObject<HTMLDivElement | null>;
  pinchContainerRef: React.RefObject<HTMLDivElement | null>;
  actionPanelRef: React.RefObject<HTMLDivElement | null>;
  swiperRef: React.MutableRefObject<SwiperType | null>;
  mobileThumbnailsRef: React.RefObject<HTMLDivElement | null>;
  // Handlers
  pinchHandlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
  };
  onSlideChange: (swiper: SwiperType) => void;
  onThumbnailClick: (img: GalleryMedia) => void;
  onContentClick: (e: React.MouseEvent) => void;
  onRegenerate?: (e: React.MouseEvent) => void;
  onUse?: (e: React.MouseEvent) => void;
  onUpscale?: (e: React.MouseEvent) => void;
  onEditImage?: (e: React.MouseEvent) => void;
  onAnimate?: (e: React.MouseEvent) => void;
  onUseVideo?: (e: React.MouseEvent) => void;
  onDownload: (e?: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onUseReference?: (url: string, isVideo: boolean) => void;
  hideDownload?: boolean;
  hideDelete?: boolean;
  hideParameters?: boolean;
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
}

export const MobileCarousel: React.FC<MobileCarouselProps> = ({
  image,
  images,
  currentIndex,
  showActionPanel,
  isVideo,
  isSvg,
  canRegenerate,
  isDownloading,
  confirmingDelete,
  pullRef,
  mobileCarouselRef,
  pinchContainerRef,
  actionPanelRef,
  swiperRef,
  mobileThumbnailsRef,
  pinchHandlers,
  onSlideChange,
  onThumbnailClick,
  onContentClick,
  onRegenerate,
  onUse,
  onUpscale,
  onEditImage,
  onAnimate,
  onUseVideo,
  onDownload,
  onDelete,
  onUseReference,
  hideDownload,
  hideDelete,
  hideParameters,
  primaryAction,
}) => {
  return (
    <div
      ref={(el) => {
        (pullRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        mobileCarouselRef.current = el;
      }}
      className={styles.mobileCarousel}
      onClick={onContentClick}
    >
      <div
        ref={pinchContainerRef as React.RefObject<HTMLDivElement>}
        className={clsx(styles.swiperContainer, showActionPanel && styles.swiperCropped)}
        {...pinchHandlers}
      >
        <Swiper
          modules={[Virtual]}
          virtual
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
          onSlideChange={onSlideChange}
          initialSlide={currentIndex >= 0 ? currentIndex : 0}
          slidesPerView={1}
          spaceBetween={16}
          centeredSlides
          touchRatio={0.6}
          touchAngle={45}
          cssMode={false}
          resistanceRatio={0.85}
        >
          {images.map((img, index) => (
            <SwiperSlide key={img.id} virtualIndex={index}>
              {img.type === 'video' ? (
                <video
                  src={index === currentIndex ? img.url : `${img.url}#t=0.001`}
                  poster={img.thumbnailUrl}
                  className={styles.mobileVideo}
                  controls={index === currentIndex}
                  playsInline
                  loop
                  muted={index !== currentIndex}
                  preload={index === currentIndex ? 'auto' : 'metadata'}
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : (
                <ProgressiveSlideImage img={img} isActive={index === currentIndex} />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Thumbnail strip */}
      <div data-role="thumb-strip" className={clsx(styles.mobileThumbnailStrip, showActionPanel && styles.thumbnailStripHidden)}>
        <div ref={mobileThumbnailsRef as React.RefObject<HTMLDivElement>} className={clsx(styles.mobileThumbnailsContainer, 'hideScrollbar')}>
          {images.map((img) => (
            <button
              key={img.id}
              className={clsx(
                styles.mobileThumbnailButton,
                image.id === img.id && styles.active,
                img.type === 'video' && styles.videoThumbnail
              )}
              onClick={() => onThumbnailClick(img)}
            >
              {img.type === 'video' && !img.thumbnailUrl ? (
                <video
                  src={`${img.url}#t=0.001`}
                  className={styles.mobileThumbnailImage}
                  muted
                  playsInline
                  preload="metadata"
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : (
                <OptimizedImage
                  src={img.thumbnailUrl || img.url}
                  alt=""
                  className={styles.mobileThumbnailImage}
                  sizes="64px"
                  width={160}
                />
              )}
              {img.type === 'video' && (
                <div className={styles.mobileThumbnailPlayIcon}>
                  <i className="fa-solid fa-play" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Inline action panel */}
      <ActionPanel
        image={image}
        isVideo={isVideo}
        isSvg={isSvg}
        canRegenerate={canRegenerate}
        isDownloading={isDownloading}
        confirmingDelete={confirmingDelete}
        showActionPanel={showActionPanel}
        onRegenerate={onRegenerate}
        onUse={onUse}
        onUpscale={onUpscale}
        onEditImage={onEditImage}
        onAnimate={onAnimate}
        onUseVideo={onUseVideo}
        onDownload={onDownload}
        onDelete={onDelete}
        panelRef={actionPanelRef}
        onUseReference={onUseReference}
        hideDownload={hideDownload}
        hideDelete={hideDelete}
        hideParameters={hideParameters}
        primaryAction={primaryAction}
      />

      {/* Spacer */}
      <div data-role="spacer" className={clsx(styles.mobileSpacerBottom, showActionPanel && styles.spacerHidden)} />
    </div>
  );
};
